import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useFileList } from '../../../hooks/use-file-list';
import { useGetFilePaths } from '../../../redux/api/appConfigApi';
import {
  useGetBugReportById,
  usePostBugReportMessage,
} from '../../../redux/api/bugReportApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../shared/constants';
import { IAppFile } from '../../../types/interfaces/app-file';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';

export function useBugReport(reportId: string) {
  const { t } = useTranslation();
  const {
    data: report,
    refetch,
    isLoading: isLoadingReport,
    isFetching: isFetchingReport,
    isError: isReportError,
    error: reportError,
  } = useGetBugReportById(reportId);
  const [postMessage] = usePostBugReportMessage();
  const [newMessage, setMessage] = useState<string>('');
  const { data: user } = useGetMyDetails();
  const { data: filePaths } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);
  const {
    filesToUpload: newPostFilesToUpload,
    onDeleteFile: onDeleteNewPostFile,
    onUploadFiles: onUploadNewPostFiles,
    resetFilesState: resetNewPostFilesState,
  } = useFileList([]);

  async function onSendMessage() {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      setIsLoading(true);
      const uploadedFiles: IAppFile[] = await Promise.all(
        newPostFilesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.bugReportMessages,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      await postMessage({
        reportId: report.id,
        body: {
          message: {
            userId: user?.id,
            reportId,
            message: newMessage,
          },
          filesToUpload: uploadedFiles,
        },
      }).unwrap();

      setMessage('');
      resetNewPostFilesState();
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or sending bug report message:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      setIsLoading(false);
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
    }
  }

  return {
    report,
    newPostFilesToUpload,
    newMessage,
    isLoading,
    setMessage,
    onSendMessage,
    onDeleteNewPostFile,
    onUploadNewPostFiles,
    refetch,
    isLoadingReport,
    isFetchingReport,
    isReportError,
    reportError,
  };
}
